import { useState } from 'react';
import { activeOddsLevel, type IframePoolOfferOdds, type VolatilityLevel } from '../../../../modules/api';
import { formatPrice } from '../../../../modules/currency';
import { ProvablyFairSection } from '../../../../modules/fairness/ui';
import { OddsSheetRow } from './OddsSheetRow';
import { PackStyleTabs } from './PackStyleTabs';

type Props = {
  odds: IframePoolOfferOdds;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
  onClose: () => void;
  titleId: string;
};

const PRICE_DECIMALS = 2;
const APPLY_BUTTON_CLASS = 'bg-[var(--co-OddsSheet-applyFill,#FFFFFF)] text-[var(--co-OddsSheet-applyText,#101114)]';

/**
 * "Choose your pack style" (partner mockup): style tabs, the level's min /
 * max value and one row per rarity. Shared by the mobile sheet and the
 * desktop popover. A tab only previews its style: the footer always reads
 * "Apply" (partner feedback 2026-10-27, was Close until the pick changed) and
 * commits a changed pick, so dismissing the sheet drops the preview.
 */
export function OddsStyleContent({ odds, volatility, onVolatilityChange, onClose, titleId }: Props) {
  const [draft, setDraft] = useState(volatility);
  const isChanged = draft !== volatility;
  const level = activeOddsLevel(odds, draft);
  const handleFooterClick = () => {
    if (isChanged) onVolatilityChange(draft);
    onClose();
  };
  // Min / max sit at the Figma 24px from a 400px-wide screen; narrower phones
  // drop to 20px so a long currency ("500.00 COCO") still fits on one line.
  return (
    <div className="flex flex-col">
      <h2 id={titleId} className="text-[20px] font-semibold leading-[28px] tracking-[-0.45px] text-[var(--co-OddsSheet-text,#FFFFFF)]">
        Choose your pack style
      </h2>
      <div className="mt-4">
        <PackStyleTabs value={draft} onChange={setDraft} />
      </div>
      <dl className="mt-5 flex items-stretch gap-4">
        <div className="flex min-w-0 flex-col-reverse gap-1">
          <dt className="text-[12px] leading-[16px] font-medium text-[var(--co-OddsSheet-mutedText,#6D707C)]">Min Value</dt>
          <dd className="truncate text-[20px] leading-[28px] min-[400px]:text-[24px] min-[400px]:leading-[32px] font-semibold tracking-[0.07px] text-[var(--co-OddsSheet-mutedText,#6D707C)]">
            {formatPrice(level.minValue, PRICE_DECIMALS)}
          </dd>
        </div>
        <span aria-hidden className="w-px bg-[var(--co-OddsSheet-surface,#1A1B1F)]" />
        <div className="flex min-w-0 flex-col-reverse gap-1">
          <dt className="text-[12px] leading-[16px] font-medium text-[var(--co-OddsSheet-mutedText,#6D707C)]">Max Value</dt>
          <dd className="truncate text-[20px] leading-[28px] min-[400px]:text-[24px] min-[400px]:leading-[32px] font-semibold tracking-[0.07px] text-[var(--co-OddsSheet-text,#FFFFFF)]">
            {formatPrice(level.maxValue, PRICE_DECIMALS)}
          </dd>
        </div>
      </dl>
      <div className="mt-6 flex flex-col gap-6">
        {level.tiers.map((tier) => (
          <OddsSheetRow key={`${tier.tierName}-${tier.range}`} name={tier.tierName} range={tier.range} percentage={tier.percentage} />
        ))}
      </div>
      <ProvablyFairSection offerId={odds.id} skin="sleek" />
      <button
        type="button"
        onClick={handleFooterClick}
        className={`mt-10 h-12 w-full rounded-xl text-[16px] font-semibold tracking-[-0.31px] ${APPLY_BUTTON_CLASS}`}
      >
        Apply
      </button>
    </div>
  );
}
